import { useMemo } from 'react'
import { mergeWith, zipWith } from 'lodash'
import { ServiceResultWith } from 'schemas/ServiceResultSchema'
import { AnnotatedMetricResult } from 'schemas/AnnotatedMetricResultSchema'
import { MetricSeriesStrings } from 'enums/MetricSeries'
import MetricTimeUnit, { MetricTimeUnitStrings } from 'enums/MetricTimeUnit'
import { getSeriesToValuesByTimeUnit, getSeriesToStatusByTimeUnit } from './utils'

const mergeSeriesToValues = (seriesToValuesArray: Record<MetricSeriesStrings, number[]>[]) =>
  seriesToValuesArray.reduce((accumulator, current) => {
    mergeWith(accumulator, current, (objValues: number[], srcValues: number[]) =>
      zipWith(objValues, srcValues, (a: number, b: number) => (a || 0) + (b || 0))
    )
    return accumulator
  }, {})

export const useTopicsResult = (
  serviceResults: ServiceResultWith<AnnotatedMetricResult>[],
  trendTimeUnit: MetricTimeUnitStrings
) => {
  const mergedTotalResult = useMemo(() => {
    const getSeriesToValuesByAll = getSeriesToValuesByTimeUnit(MetricTimeUnit.ALL)
    const topicsTotalResults =
      serviceResults?.map((currentData) => getSeriesToValuesByAll(currentData)) || []
    return mergeSeriesToValues(topicsTotalResults)
  }, [serviceResults])

  const mergedTrendResult = useMemo(() => {
    const getSeriesToValuesByInterval = getSeriesToValuesByTimeUnit(trendTimeUnit)
    const topicsTrendResults =
      serviceResults?.map((currentData) => getSeriesToValuesByInterval(currentData)) || []
    return mergeSeriesToValues(topicsTrendResults)
  }, [serviceResults, trendTimeUnit])

  return [mergedTotalResult, mergedTrendResult]
}

export const useSelectedTopicResult = (
  serviceResult: ServiceResultWith<AnnotatedMetricResult>,
  trendTimeUnit: MetricTimeUnitStrings
) => {
  const totalResult = useMemo(() => {
    const getSeriesToValuesByAll = getSeriesToValuesByTimeUnit(MetricTimeUnit.ALL)
    return getSeriesToValuesByAll(serviceResult)
  }, [serviceResult])
  const trendResult = useMemo(() => {
    const getSeriesToValuesByInterval = getSeriesToValuesByTimeUnit(trendTimeUnit)
    return getSeriesToValuesByInterval(serviceResult)
  }, [serviceResult, trendTimeUnit])

  return [totalResult, trendResult]
}

export const useSelectedTopicStatus = (
  serviceResult: ServiceResultWith<AnnotatedMetricResult>,
  trendTimeUnit: MetricTimeUnitStrings
) => {
  const totalStatus = useMemo(() => {
    const getSeriesToStatusByAll = getSeriesToStatusByTimeUnit(MetricTimeUnit.ALL)
    return getSeriesToStatusByAll(serviceResult)
  }, [serviceResult])
  const trendStatus = useMemo(() => {
    const getSeriesToStatusByAll = getSeriesToStatusByTimeUnit(trendTimeUnit)
    return getSeriesToStatusByAll(serviceResult)
  }, [serviceResult, trendTimeUnit])

  return [totalStatus, trendStatus]
}
